export const JENC_MAGIC = 'JARVIS-ENC/1';
export const DEFAULT_PBKDF2_ITERATIONS = 480_000;
const KEY_LENGTH = 32;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;

export interface JencKdfInfo {
  name: 'PBKDF2';
  hash: 'SHA-256';
  iterations: number;
}

export interface JencEnvelope {
  magic: string;
  salt: string;
  iv: string;
  ciphertext: string;
  kdf: JencKdfInfo;
  managedWrappedKey?: string;
}

export interface EncryptPlaintextOptions {
  pbkdf2Iterations?: number;
  managedWrappedKey?: string;
}

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto is unavailable. Open this page over HTTPS or localhost.');
  }
  return subtle;
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function deriveAesKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> {
  const subtle = getSubtle();
  const passphraseKey = await subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256'
    },
    passphraseKey,
    { name: 'AES-GCM', length: KEY_LENGTH * 8 },
    true,
    ['encrypt', 'decrypt']
  );
}

async function exportRawKey(key: CryptoKey): Promise<Uint8Array> {
  const raw = await getSubtle().exportKey('raw', key);
  return new Uint8Array(raw);
}

async function importRawAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return getSubtle().importKey('raw', raw, { name: 'AES-GCM', length: KEY_LENGTH * 8 }, false, [
    'encrypt',
    'decrypt'
  ]);
}

export function isJencPath(path: string): boolean {
  return path.trim().toLowerCase().endsWith('.jenc');
}

export function parseEnvelope(content: string): JencEnvelope | null {
  const trimmed = content.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed) as Partial<JencEnvelope>;
    if (parsed.magic !== JENC_MAGIC) return null;
    if (
      typeof parsed.salt !== 'string' ||
      typeof parsed.iv !== 'string' ||
      typeof parsed.ciphertext !== 'string' ||
      !parsed.kdf ||
      parsed.kdf.name !== 'PBKDF2' ||
      parsed.kdf.hash !== 'SHA-256' ||
      typeof parsed.kdf.iterations !== 'number'
    ) {
      return null;
    }
    return parsed as JencEnvelope;
  } catch {
    return null;
  }
}

export function detectEncryptedContent(content: string): boolean {
  return parseEnvelope(content) !== null;
}

export async function encryptPlaintext(
  plaintext: string,
  passphrase: string,
  options?: EncryptPlaintextOptions
): Promise<string> {
  if (!passphrase) throw new Error('Passphrase is required.');

  const iterations = options?.pbkdf2Iterations ?? DEFAULT_PBKDF2_ITERATIONS;
  const salt = randomBytes(SALT_LENGTH);
  const iv = randomBytes(IV_LENGTH);
  const contentKey = await deriveAesKey(passphrase, salt, iterations);
  const ciphertext = await getSubtle().encrypt(
    { name: 'AES-GCM', iv },
    contentKey,
    new TextEncoder().encode(plaintext)
  );

  const envelope: JencEnvelope = {
    magic: JENC_MAGIC,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    kdf: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations
    }
  };

  if (options?.managedWrappedKey) {
    envelope.managedWrappedKey = options.managedWrappedKey;
  }

  return `${JSON.stringify(envelope, null, 2)}\n`;
}

export async function decryptEnvelope(
  envelopeContent: string,
  passphrase: string
): Promise<string> {
  const envelope = parseEnvelope(envelopeContent);
  if (!envelope) throw new Error('Not a valid JARVIS-ENC envelope.');

  const salt = base64ToBytes(envelope.salt);
  const iv = base64ToBytes(envelope.iv);
  const ciphertext = base64ToBytes(envelope.ciphertext);
  const contentKey = await deriveAesKey(passphrase, salt, envelope.kdf.iterations);

  try {
    const plaintext = await getSubtle().decrypt({ name: 'AES-GCM', iv }, contentKey, ciphertext);
    return new TextDecoder().decode(plaintext);
  } catch {
    throw new Error('Incorrect passphrase or corrupted encrypted file.');
  }
}

export async function decryptEnvelopeWithRawKey(
  envelopeContent: string,
  rawKey: Uint8Array
): Promise<string> {
  const envelope = parseEnvelope(envelopeContent);
  if (!envelope) throw new Error('Not a valid JARVIS-ENC envelope.');
  if (rawKey.length !== KEY_LENGTH) throw new Error('Content key must be 32 bytes.');

  const iv = base64ToBytes(envelope.iv);
  const ciphertext = base64ToBytes(envelope.ciphertext);
  const contentKey = await importRawAesKey(rawKey);

  try {
    const plaintext = await getSubtle().decrypt({ name: 'AES-GCM', iv }, contentKey, ciphertext);
    return new TextDecoder().decode(plaintext);
  } catch {
    throw new Error('Managed recovery key could not decrypt this file.');
  }
}

export async function exportContentKeyBytes(
  passphrase: string,
  envelope: JencEnvelope
): Promise<Uint8Array> {
  const salt = base64ToBytes(envelope.salt);
  const contentKey = await deriveAesKey(passphrase, salt, envelope.kdf.iterations);
  return exportRawKey(contentKey);
}

export function contentKeyToBase64(key: Uint8Array): string {
  return bytesToBase64(key);
}

export function contentKeyFromBase64(value: string): Uint8Array {
  return base64ToBytes(value);
}

export function withManagedWrappedKey(envelopeContent: string, managedWrappedKey: string): string {
  const envelope = parseEnvelope(envelopeContent);
  if (!envelope) throw new Error('Not a valid JARVIS-ENC envelope.');
  return `${JSON.stringify({ ...envelope, managedWrappedKey }, null, 2)}\n`;
}
